// A week, read (round 44): for the office reviewing it, and for the worker
// once it is out of their hands. Cards on a phone, a table on a desktop, over
// the one list of days (CLAUDE.md: a change must do both).
import { minutesToHm } from '../../lib/timesheetHours';
import { clock, dayLabel } from '../../lib/timesheetWeeks';
import { CardList, RowCard } from '../ui/RowCard';
import type { TimesheetDay, TimesheetDetail } from '../../timesheetsApi';

function place(day: TimesheetDay): string | null {
  if (day.jobNumber) return `${day.jobNumber} · ${day.jobTitle ?? ''}`.trim();
  return day.location;
}

function leave(day: TimesheetDay, names: Map<string, string>): string | null {
  if (!day.leaveTypeId) return null;
  const name = names.get(day.leaveTypeId) ?? 'Leave';
  if (day.leaveMinutes === null) return day.startTime ? name : `${name} (full day)`;
  return `${name} ${minutesToHm(day.leaveMinutes)}`;
}

function times(day: TimesheetDay): string | null {
  if (!day.startTime || !day.finishTime) return null;
  return `${clock(day.startTime)} to ${clock(day.finishTime)}${day.finishesNextDay ? ' (next day)' : ''}`;
}

export function WeekReadView({ detail }: { detail: TimesheetDetail }) {
  const leaveNames = new Map(detail.leaveTypes.map((t) => [t.id, t.name]));
  const allowanceNames = new Map(detail.allowanceTypes.map((t) => [t.id, t.name]));
  const totals = detail.totals;

  return (
    <section className="panel">
      <h2>The week</h2>
      <CardList>
        {detail.days.map((day) => {
          const lines = [
            times(day) && `${times(day)}${day.breakMinutes > 0 ? ` · ${day.breakMinutes} min break` : ''}`,
            place(day),
            leave(day, leaveNames),
            day.allowanceTypeIds.length > 0 &&
              `Allowances: ${day.allowanceTypeIds.map((a) => allowanceNames.get(a) ?? 'Allowance').join(', ')}`,
            day.note,
          ].filter((v): v is string => typeof v === 'string' && v !== '');
          return (
            <RowCard
              key={day.date}
              title={dayLabel(day.date)}
              badges={
                day.workedMinutes > 0 ? (
                  <span className="text-sm font-semibold tabular-nums">{minutesToHm(day.workedMinutes)}</span>
                ) : undefined
              }
            >
              {lines.length === 0 ? <span className="muted">Nothing entered</span> : lines.map((l) => <div key={l}>{l}</div>)}
            </RowCard>
          );
        })}
      </CardList>

      <div className="table-wrap max-sm:hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th className="table-header">Day</th>
              <th className="table-header">Job or place</th>
              <th className="table-header">Start</th>
              <th className="table-header">Finish</th>
              <th className="table-header text-right">Break</th>
              <th className="table-header text-right">Worked</th>
              <th className="table-header">Leave</th>
              <th className="table-header">Allowances</th>
              <th className="table-header">Note</th>
            </tr>
          </thead>
          <tbody>
            {detail.days.map((day) => (
              <tr key={day.date}>
                <td className="whitespace-nowrap">{dayLabel(day.date)}</td>
                <td>{place(day) ?? ''}</td>
                <td className="whitespace-nowrap tabular-nums">{clock(day.startTime)}</td>
                <td className="whitespace-nowrap tabular-nums">
                  {clock(day.finishTime)}
                  {day.finishesNextDay && <span className="table-muted"> next day</span>}
                </td>
                <td className="text-right tabular-nums">
                  {day.startTime && day.breakMinutes > 0 ? minutesToHm(day.breakMinutes) : ''}
                </td>
                <td className="text-right font-medium tabular-nums">
                  {day.workedMinutes > 0 ? minutesToHm(day.workedMinutes) : ''}
                </td>
                <td>{leave(day, leaveNames) ?? ''}</td>
                <td>{day.allowanceTypeIds.map((a) => allowanceNames.get(a) ?? 'Allowance').join(', ')}</td>
                <td className="max-w-xs break-words">{day.note ?? ''}</td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td>Total</td>
              <td />
              <td />
              <td />
              <td className="text-right tabular-nums">{totals.breakMinutes > 0 ? minutesToHm(totals.breakMinutes) : ''}</td>
              <td className="text-right tabular-nums">{minutesToHm(totals.workedMinutes)}</td>
              <td colSpan={3}>
                {[
                  totals.leaveMinutes > 0 ? `${minutesToHm(totals.leaveMinutes)} leave` : null,
                  totals.fullLeaveDays > 0
                    ? `${totals.fullLeaveDays} full day${totals.fullLeaveDays === 1 ? '' : 's'} of leave`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' + ')}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="!mb-0 mt-3 text-sm font-semibold tabular-nums text-gray-900 sm:hidden">
        Worked {minutesToHm(totals.workedMinutes)}
        {totals.leaveMinutes > 0 && <> · Leave {minutesToHm(totals.leaveMinutes)}</>}
        {totals.fullLeaveDays > 0 && <> · {totals.fullLeaveDays} day{totals.fullLeaveDays === 1 ? '' : 's'} leave</>}
      </p>
      {detail.sheet.note && (
        <p className="!mb-0 mt-3 text-sm text-gray-700">
          <span className="font-medium">Note for the week:</span> {detail.sheet.note}
        </p>
      )}
    </section>
  );
}
